import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronRight } from 'lucide-react';
type AnswerState = {
  q1: string;
  q2: string[];
  q3: string;
  q4: string;
  q5: string;
};
const questions = [{
  id: 'q1',
  title: '¿Cuánto tiempo llevás sintiendo que la suerte no está de tu lado?',
  type: 'single',
  options: [{
    label: 'Menos de 6 meses',
    score: 1
  }, {
    label: '6 meses a 1 año',
    score: 2
  }, {
    label: '1 a 3 años',
    score: 3
  }, {
    label: 'Más de 3 años',
    score: 4
  }, {
    label: 'Toda mi vida',
    score: 5
  }]
}, {
  id: 'q2',
  title: '¿Qué intentaste antes que no te funcionó?',
  subtitle: 'Seleccioná todas las que apliquen',
  type: 'multiple',
  options: [{
    label: 'Meditación',
    score: 0.5
  }, {
    label: 'Cristales',
    score: 0.5
  }, {
    label: 'Oración',
    score: 0.5
  }, {
    label: 'Afirmaciones',
    score: 0.5
  }, {
    label: 'Aceites esenciales',
    score: 0.5
  }, {
    label: 'Terapia',
    score: 0.5
  }]
}, {
  id: 'q3',
  title: '¿En qué área sentís más el bloqueo?',
  type: 'single',
  options: [{
    label: 'Dinero',
    score: 0
  }, {
    label: 'Amor',
    score: 0
  }, {
    label: 'Salud',
    score: 0
  }, {
    label: 'Carrera',
    score: 0
  }, {
    label: 'Familia',
    score: 0
  }]
}, {
  id: 'q4',
  title: '¿Creés que hay algo invisible que está afectando tu vida?',
  type: 'single',
  options: [{
    label: 'Sí, definitivamente',
    score: 2
  }, {
    label: 'Probablemente',
    score: 1.5
  }, {
    label: 'No estoy segura pero algo no está bien',
    score: 1
  }, {
    label: 'No creo pero quiero entender por qué nada funciona',
    score: 0
  }]
}, {
  id: 'q5',
  title: '¿Qué tan lista estás para cambiar esto?',
  type: 'single',
  options: [{
    label: 'Estoy desesperada',
    score: 0
  }, {
    label: 'Estoy cansada de intentar sin resultados',
    score: 0
  }, {
    label: 'Estoy curiosa',
    score: 0
  }, {
    label: 'Solo estoy mirando',
    score: 0
  }]
}];
export function QuizPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0-4: questions, 5: email
  const [answers, setAnswers] = useState<AnswerState>({
    q1: '',
    q2: [],
    q3: '',
    q4: '',
    q5: ''
  });
  const [email, setEmail] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  const handleSingleSelect = (questionId: keyof AnswerState, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    setTimeout(() => {
      setStep(prev => prev + 1);
    }, 400);
  };
  const handleMultiSelect = (questionId: 'q2', value: string) => {
    setAnswers(prev => {
      const current = prev[questionId];
      if (current.includes(value)) {
        return {
          ...prev,
          [questionId]: current.filter(v => v !== value)
        };
      } else {
        return {
          ...prev,
          [questionId]: [...current, value]
        };
      }
    });
  };
  const calculateScore = (): number => {
    let total = 0;
    const q1Opt = questions[0].options.find(o => o.label === answers.q1);
    if (q1Opt) total += q1Opt.score;
    answers.q2.forEach(ans => {
      const opt = questions[1].options.find(o => o.label === ans);
      if (opt) total += opt.score;
    });
    const q4Opt = questions[3].options.find(o => o.label === answers.q4);
    if (q4Opt) total += q4Opt.score;
    return Number(Math.min(10, Math.max(1, total)).toFixed(1));
  };
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    const score = calculateScore();
    // Store score and answers in sessionStorage for the diagnosis page
    sessionStorage.setItem('quizScore', String(score));
    sessionStorage.setItem('quizArea', answers.q3);
    sessionStorage.setItem('quizEmail', email);
    // Navigate to diagnosis page
    navigate('/diagnosis');
  };
  const renderQuestion = () => {
    const q = questions[step];
    const isMulti = q.type === 'multiple';
    const currentAnswer = answers[q.id as keyof AnswerState];
    return <motion.div key={`step-${step}`} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} transition={{
      duration: 0.3
    }} className="w-full max-w-lg mx-auto flex flex-col" data-id="element-385">
        <h2 className="font-serif text-3xl md:text-4xl text-cream text-center mb-2 leading-tight" data-id="element-386">
          {q.title}
        </h2>
        {q.subtitle && <p className="text-warm-gold text-sm text-center mb-8 uppercase tracking-wider font-medium" data-id="element-387">
            {q.subtitle}
          </p>}
        {!q.subtitle && <div className="h-8" data-id="element-388"></div>}

        <div className="flex flex-col space-y-3" data-id="element-389">
          {q.options.map(opt => {
          const isSelected = isMulti ? (currentAnswer as string[]).includes(opt.label) : currentAnswer === opt.label;
          return <button key={opt.label} onClick={() => isMulti ? handleMultiSelect(q.id as 'q2', opt.label) : handleSingleSelect(q.id as keyof AnswerState, opt.label)} className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${isSelected ? 'bg-warm-gold/20 border-warm-gold text-cream' : 'bg-navy-light/50 border-gold/20 text-cream/80 hover:border-warm-gold/60 hover:bg-navy-light'}`} data-id="element-390">
                <span className="text-lg" data-id="element-391">{opt.label}</span>
                {isSelected && <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="w-6 h-6 rounded-full bg-warm-gold flex items-center justify-center" data-id="element-392">
                    <Check className="w-4 h-4 text-navy font-bold" data-id="element-393" />
                  </motion.div>}
              </button>;
        })}
        </div>

        {isMulti && (currentAnswer as string[]).length > 0 && <motion.button initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} onClick={() => setStep(prev => prev + 1)} className="mt-8 w-full py-4 bg-warm-gold text-navy font-bold text-lg rounded-full hover:bg-gold transition-colors flex items-center justify-center" data-id="element-394">
            Continuar <ChevronRight className="w-5 h-5 ml-1" data-id="element-395" />
          </motion.button>}
      </motion.div>;
  };
  const renderEmail = () => <motion.div key="step-email" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="w-full max-w-lg mx-auto flex flex-col items-center text-center" data-id="element-396">
      <div className="w-20 h-20 bg-warm-gold/10 rounded-full flex items-center justify-center mb-6" data-id="element-397">
        <Check className="w-10 h-10 text-warm-gold" data-id="element-398" />
      </div>
      <h2 className="font-serif text-3xl md:text-4xl text-cream mb-4" data-id="element-399">
        Tu resultado está listo...
      </h2>
      <p className="text-cream/80 text-lg mb-8" data-id="element-400">
        Ingresá tu email para ver tu nivel de interferencia personalizado.
      </p>

      <form onSubmit={handleEmailSubmit} className="w-full flex flex-col space-y-4" data-id="element-401">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Tu mejor email..." required className="w-full bg-navy-light border-2 border-gold/30 rounded-xl px-6 py-4 text-cream placeholder-cream/40 focus:outline-none focus:border-warm-gold transition-colors text-lg" data-id="element-402" />
        <button type="submit" className="w-full py-4 bg-warm-gold text-navy font-bold text-xl rounded-xl hover:bg-gold transition-colors" data-id="element-403">
          Ver Mi Resultado →
        </button>
        <p className="text-cream/40 text-sm mt-4" data-id="element-404">
          No spam. Solo tu resultado.
        </p>
      </form>
    </motion.div>;
  return <div className="min-h-screen bg-navy font-sans text-cream selection:bg-warm-gold/30 selection:text-cream flex flex-col" data-id="element-405">
      {/* Header / Progress */}
      {step < 5 && <div className="w-full pt-8 pb-4 px-4" data-id="element-406">
          <div className="max-w-lg mx-auto" data-id="element-407">
            <div className="flex justify-between items-center mb-3" data-id="element-408">
              <span className="text-warm-gold font-serif italic" data-id="element-409">
                The Lucky Flush
              </span>
              <span className="text-cream/60 text-sm font-medium" data-id="element-410">
                Paso {step + 1} de 5
              </span>
            </div>
            <div className="w-full h-2 bg-navy-light rounded-full overflow-hidden" data-id="element-411">
              <motion.div className="h-full bg-warm-gold" initial={{
            width: `${step / 5 * 100}%`
          }} animate={{
            width: `${(step + 1) / 5 * 100}%`
          }} transition={{
            duration: 0.3
          }} data-id="element-412" />
            </div>
          </div>
        </div>}

      {/* Main Content Area */}
      <main className="flex-1 flex items-center justify-center px-4 py-8" data-id="element-413">
        <AnimatePresence mode="wait" data-id="element-414">
          {step < 5 && renderQuestion()}
          {step === 5 && renderEmail()}
        </AnimatePresence>
      </main>
    </div>;
}